import React from 'react'
import { toast } from 'react-toastify'

type AmountStepProps = {
    amountInputInUserCurrency: string
    setAmountInputInUserCurrency: (value: string) => void
    targetAmountInUsd: number
    setTargetAmountInUsd: (value: number) => void
    presetAmounts: number[]
    currency: string
    currencySymbol: string
    convertFromUSD: (usd: number) => number
    formatAmountWithoutCurrency: (value: number) => string
    minAmount?: number
    maxAmount?: number
}

const AmountStep: React.FC<AmountStepProps> = ({
                                                   amountInputInUserCurrency,
                                                   setAmountInputInUserCurrency,
                                                   targetAmountInUsd,
                                                   setTargetAmountInUsd,
                                                   presetAmounts,
                                                   currency,
                                                   currencySymbol,
                                                   convertFromUSD,
                                                   formatAmountWithoutCurrency,
                                                   minAmount,
                                                   maxAmount,
                                               }) => {
    const handleInputChange = (raw: string) => {
        let value = raw.replace(/[^0-9.,]/g, '')

        if (value.includes('.') || value.includes(',')) {
            const separator = value.includes('.') ? '.' : ','
            const parts = value.split(separator)
            parts[1] = parts[1].slice(0, 2)
            value = parts.join(separator)
        }

        setAmountInputInUserCurrency(value)

        const normalized = value.replace(',', '.')
        const parsed = parseFloat(normalized)

        if (!isNaN(parsed)) {
            setTargetAmountInUsd(parsed)
        }
    }

    const handleBlur = () => {
        const value = parseFloat(amountInputInUserCurrency.replace(',', '.'))
        if (minAmount !== undefined && value < minAmount) {
            toast.info(`Minimum deposit is ${minAmount.toFixed(2)} ${currencySymbol}`)
        }
        if (maxAmount !== undefined && value > maxAmount) {
            toast.info(`Maximum deposit is ${maxAmount.toFixed(2)} ${currencySymbol}`)
        }
    }

    return (
        <div className="flex flex-col gap-3">
            {/* Inline Input & Limits Header */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base font-bold text-neutral-400 select-none">
                        {currencySymbol}
                    </span>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={amountInputInUserCurrency}
                        onChange={e => handleInputChange(e.target.value)}
                        onBlur={handleBlur}
                        placeholder="0.00"
                        className="w-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700/80 rounded-xl pl-8 pr-3 py-2 text-base font-bold text-neutral-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    />
                </div>

                <div className="flex flex-col justify-center text-right text-[11px] text-neutral-400 bg-white dark:bg-neutral-900/60 px-3 py-1.5 rounded-xl border border-neutral-200 dark:border-neutral-800 whitespace-nowrap">
                    <span className="text-[9px] uppercase font-bold text-neutral-400 tracking-wider">Limits</span>
                    <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                        Min: {minAmount !== undefined ? `${currencySymbol}${minAmount.toFixed(2)}` : '-'} · Max: {maxAmount !== undefined ? `${currencySymbol}${maxAmount.toFixed(2)}` : '-'}
                    </span>
                </div>
            </div>

            {/* Presets Grid: 2 rows of 3 buttons */}
            <div className="grid grid-cols-3 gap-2">
                {presetAmounts.map(preset => {
                    const converted = convertFromUSD(preset)
                    const disabled =
                        (minAmount !== undefined && converted < minAmount) ||
                        (maxAmount !== undefined && converted > maxAmount)
                    const isSelected = targetAmountInUsd === preset

                    return (
                        <button
                            key={preset}
                            disabled={disabled}
                            type="button"
                            onClick={() => {
                                setTargetAmountInUsd(preset)
                                setAmountInputInUserCurrency(formatAmountWithoutCurrency(converted))
                            }}
                            className={`py-2 px-3 rounded-lg border text-center transition-all ${
                                isSelected
                                    ? 'border-blue-500 bg-blue-500/10 text-blue-500 font-bold ring-1 ring-blue-500 shadow-xs'
                                    : 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/60 hover:border-neutral-300 dark:hover:border-neutral-700 text-neutral-700 dark:text-neutral-300'
                            } ${disabled ? 'opacity-35 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            <span className="text-xs font-bold block">
                                {currencySymbol}{formatAmountWithoutCurrency(converted)}
                                {currency !== 'USD' && (
                                    <span className="text-[10px] font-normal opacity-70 ml-1">
                                        (${preset})
                                    </span>
                                )}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default AmountStep